import produce from 'immer';
import enrichClassifications from './enrichClassifications';
import * as matchingRules from './matchingRules';
import * as sortingRules from './sortingRules';
import getUniqueCategoriesAndDescriptors from './unique';
export const lexicalMapCompare = (a, b) => String(a[0].localeCompare(b[0]));
const prepareComparisonDocs = (anchorDocId, comparisonDocuments) => {
    const documents = [];
    const anchorTags = new Map();
    const extraTags = new Map();
    if (!anchorDocId || comparisonDocuments.length === 0) {
        return { tagsColumn: new Map(), documents, categories: [], descriptors: [] };
    }
    const { categories, descriptors } = getUniqueCategoriesAndDescriptors(comparisonDocuments);
    comparisonDocuments = enrichClassifications(comparisonDocuments);
    const anchorDoc = comparisonDocuments === null || comparisonDocuments === void 0 ? void 0 : comparisonDocuments.find(doc => doc.documentComparisonFileId === anchorDocId);
    if (!anchorDoc) {
        throw Error(`Failed To Find Anchor Doc: ${anchorDocId}`);
    }
    anchorDoc.classifications.forEach(classification => {
        anchorTags.set(classification.name, classification);
    });
    comparisonDocuments.forEach(doc => {
        let classificationMap = new Map();
        const classifications = Array.from(doc.classifications);
        classifications.forEach(classification => {
            const matchingAnchorKeys = getClassificationMapKeysByRules(anchorTags, classification);
            if (!(matchingAnchorKeys === null || matchingAnchorKeys === void 0 ? void 0 : matchingAnchorKeys.length)) {
                extraTags.set(classification.name, classification);
            }
            const classificationMapKeys = matchingAnchorKeys || [{
                    rowKey: classification.name,
                    tabKey: classification.name,
                }];
            classificationMap = addClassificationToMap(classificationMap, classificationMapKeys, classification);
        });
        const newDoc = produce(doc, draft => {
            draft.classificationMap = classificationMap;
            delete draft.classifications;
        });
        documents.push(newDoc);
    });
    const tagsColumn = new Map();
    const orderedClassifierKeys = sortTagsByRules(anchorTags, extraTags);
    for (const classifier of orderedClassifierKeys) {
        tagsColumn.set(classifier, anchorTags.get(classifier) || extraTags.get(classifier));
    }
    return { tagsColumn, documents, categories, descriptors };
};
const getClassificationMapKeysByRules = (anchorTags, classification) => {
    const exactMatchKey = matchingRules.getExactMatchKey(anchorTags, classification);
    if (exactMatchKey)
        return [exactMatchKey];
    const matchingParentKey = matchingRules.getMatchingParentKey(anchorTags, classification);
    if (matchingParentKey)
        return [matchingParentKey];
    const matchingChildKeys = matchingRules.getMatchingChildKeys(anchorTags, classification);
    if (matchingChildKeys === null || matchingChildKeys === void 0 ? void 0 : matchingChildKeys.length)
        return matchingChildKeys;
    const exactMatchDescriptor = matchingRules.getExactMatchDescriptorInCategoryFamily(anchorTags, classification);
    if (exactMatchDescriptor === null || exactMatchDescriptor === void 0 ? void 0 : exactMatchDescriptor.length)
        return exactMatchDescriptor;
    const matchingDescriptorParent = matchingRules.getDescriptorParentInCategoryFamily(anchorTags, classification);
    if (matchingDescriptorParent === null || matchingDescriptorParent === void 0 ? void 0 : matchingDescriptorParent.length)
        return matchingDescriptorParent;
    const matchingDescriptorChildren = matchingRules.getDescriptorChildrenInCategoryFamily(anchorTags, classification);
    if (matchingDescriptorChildren === null || matchingDescriptorChildren === void 0 ? void 0 : matchingDescriptorChildren.length)
        return matchingDescriptorChildren;
};
const addClassificationToMap = (classificationMap, classificationMapKeys, classification) => {
    for (const classificationMapKey of classificationMapKeys) {
        const existingRow = classificationMap.get(classificationMapKey.rowKey);
        if (existingRow) {
            const existingTabIndex = existingRow.findIndex(tab => tab.name === classificationMapKey.tabKey);
            if (existingTabIndex !== -1) {
                existingRow[existingTabIndex].classifications.push(classification);
            }
            else {
                const newTab = {
                    name: classificationMapKey.tabKey,
                    classifications: [classification],
                };
                existingRow.push(newTab);
            }
        }
        else {
            const newRow = [{
                    name: classificationMapKey.tabKey,
                    classifications: [classification],
                }];
            classificationMap.set(classificationMapKey.rowKey, newRow);
        }
    }
    return classificationMap;
};
const sortTagsByRules = (anchorTags, extraTags) => {
    let orderedClassifierKeys = [];
    orderedClassifierKeys = sortingRules.sortAnchorTagsByAnchorDocOrder(orderedClassifierKeys, anchorTags);
    orderedClassifierKeys = sortingRules.sortExtraTagsGroupedByCategory(orderedClassifierKeys, extraTags);
    return orderedClassifierKeys;
};
export default prepareComparisonDocs;
